import { useMemo, useState } from "react";
import {
  CupsDetail,
  GetSuppliesDetailRequestBody,
} from "../../../../core/models/suppliesDetail/suppliesDetail.model";
import { FormattedOwnerData } from "../../../../core/models/ownerData/ownerData.model";
import { getSuppliesDetail as getSuppliesDetailFetch } from "../../../../core/services/suppliesDetail/suppliesDetail.service";
import { getBase64 } from "../managements.util";
import { BipValidationResponse } from "../../../../core/models/managements/managements.model";
import { useTranslation } from "react-i18next";

const useOcrValidation = () => {
  const [isOcrLoading, setIsOcrLoading] = useState<boolean>(false);
  const [supplyData, setSupplyData] = useState<CupsDetail | null>(null);
  const [ocrError, setOcrError] = useState<
    "response_ko" | "server_error" | null
  >(null);
  const { t } = useTranslation();
  const [ocrErrorMessageKeys, setOcrErrorMessageKeys] = useState<string[]>([]);
  const ocrErrorMessage = useMemo(
    () => ocrErrorMessageKeys.map((key) => t(`Gestiones.errores_ocr.${key}`)),
    [ocrErrorMessageKeys, t],
  );

  const formatOcrErrorMessage = (res: BipValidationResponse): string[] => {
    const { validation_result: validationResult } = res;

    if (validationResult.message === "Validation ko") {
      const { result } = validationResult;
      const failedValidations = Object.entries(result)
        .filter((item) => !item[1])
        .map((item) => item[0]);
      let validationMessagesArray: string[] = [];

      if (res.document_type === "cambio_potencia") {
        validationMessagesArray = [
          ...new Set(
            failedValidations.map((val) => {
              if (val.includes("tarifa") || val.includes("potencia")) {
                return "tarifa_potencia";
              } else if (val.includes("representante")) {
                return "representante";
              } else if (val === "localidad_y_fecha_documento") {
                return "firma";
              } else return val;
            }),
          ),
        ];
      }

      if (res.document_type === "baja_cese") {
        validationMessagesArray = [
          ...new Set(
            failedValidations.map((val) => {
              if (val === "nombre" || val === "dni") {
                return "datos_representante";
              } else if (val === "localidad_y_fecha_documento") {
                return "firma";
              } else return val;
            }),
          ),
        ];
      }

      if (res.document_type === "cambio_domiciliacion") {
        validationMessagesArray = [
          ...new Set(
            failedValidations.map((val) => {
              if (val === "cp_poblacion" || val === "pais") {
                return "direccion";
              }
              if (val === "iban" || val === "swift") {
                return "iban-swift";
              }
              if (val === "localidad_firma" || val === "fecha_firma") {
                return "firma";
              } else return val;
            }),
          ),
        ];
      }

      return validationMessagesArray;
    } else return [];
  };

  const formatOcrErrorMessageText = (res: BipValidationResponse): string =>
    formatOcrErrorMessage(res)
      .map((key) => t(`Gestiones.errores_ocr.${key}`))
      .join(", ");

  const getSuppliesData = async (
    token: string,
    abortSignal: AbortSignal,
    { isAdministrator, idClient, idAdministrator }: FormattedOwnerData,
    idCups?: string,
    contractId?: string,
  ) => {
    const getSuppliesRequestBody: GetSuppliesDetailRequestBody = {
      userid: isAdministrator ? (idAdministrator ?? "") : (idClient ?? ""),
      isAdmin: isAdministrator?.toString() ?? "",
      cupsContractId: contractId?.toString() ?? "",
      cupsId: idCups ?? "",
    };

    try {
      const suppliesDetail = await getSuppliesDetailFetch(
        getSuppliesRequestBody,
        token,
        abortSignal,
      );
      setSupplyData(suppliesDetail);
      return suppliesDetail;
    } catch {
      console.error("Couldn't fetch supplies details");
    }
  };

  const getBase64file = async (file: File | null) => {
    if (!file) return;

    return (
      file &&
      (await getBase64(file).then((res) => (res as string).split("base64,")[1]))
    );
  };

  return {
    isOcrLoading,
    setIsOcrLoading,
    supplyData,
    setSupplyData,
    ocrError,
    setOcrError,
    getSuppliesData,
    getBase64file,
    formatOcrErrorMessage,
    ocrErrorMessage,
    setOcrErrorMessageKeys,
    formatOcrErrorMessageText,
  };
};

export default useOcrValidation;
